import {
  defineNuxtRouteMiddleware,
  useNuxtApp,
  useRuntimeConfig,
} from '#imports'

import { useHttpFetch } from '@backmarket/nuxt-module-http-v2/useHttpFetch'
import { useLogger } from '@backmarket/nuxt-module-logger/useLogger'

import hashByRoute from '~~/.translations/hash-by-route.json'

import { useI18nLocale } from '../composables/useI18nLocale'
import { useIsTranslationsSplitV2Enabled } from '../composables/useIsTranslationsSplitV2Enabled'
import { useVisitedRoutes } from '../composables/useVisitedRoutes'
import { CLIENT_GLOBAL_KEY } from '../constants'

/**
 * Example:
 * hashByRoute = {
 *  "home": "83b9e990e5cee382a778d101ac838d2925bd84cd40eb0cf3c9e8812f5222c037",
 *  "product": "e8540a5e56cdfb25c45304bcf59eceaf992a761735cfdee9be24f59fc6c09ac9",
 *  "product-details": "e8540a5e56cdfb25c45304bcf59eceaf992a761735cfdee9be24f59fc6c09ac9",
 *  "master-pp": "e8540a5e56cdfb25c45304bcf59eceaf992a761735cfdee9be24f59fc6c09ac9",
 *  "landing": "193b43caf7a48144cac6f4a7d0fd8663cba69baf0cfc2760de74fc8030f466c7"
 *  ...
 * }
 * */

export default defineNuxtRouteMiddleware(async (to) => {
  if (import.meta.server) return

  const logger = useLogger()
  try {
    const app = useNuxtApp()

    if (app.isHydrating) {
      return
    }

    const config = useRuntimeConfig()
    const { appName } = config.public.i18n

    const translationsSplitV2Enabled = useIsTranslationsSplitV2Enabled()

    // Ignore the route middleware if the translations-split-v2 feature is not enabled
    if (!translationsSplitV2Enabled) {
      return
    }

    const { hasBeenVisited, visitRoute } = useVisitedRoutes()

    const routeName = String(to.name)
    // If we navigate to a not found route, we can do an early return
    // A 404 page will be displayed and it only relies on the shared translations
    if (!routeName) {
      return
    }

    const hash = hashByRoute[routeName as keyof typeof hashByRoute]

    if (!hash) {
      return
    }

    // If the route has already been visited, its translations have already been loaded and we can do an early return
    if (hasBeenVisited(hash)) {
      return
    }

    const locale: string = useI18nLocale()

    const $httpFetch = useHttpFetch()

    const routeTranslations = await $httpFetch(
      '/:locale/:appName/translations/:hash',
      {
        pathParams: {
          locale,
          appName,
          hash,
        },
        baseURL: window.location.origin,
      },
    )

    window[CLIENT_GLOBAL_KEY].translations = {
      ...(window[CLIENT_GLOBAL_KEY].translations || {}),
      ...(routeTranslations || {}),
    }

    visitRoute(hash)
  } catch (err) {
    logger.error(
      '[Translations Split][Middleware] Error loading route translations',
      {
        error: err as Error,
        owners: [
          'bot-chapter-frontend-core-pastrami',
          'bot-squad-content-front',
          'bot-squad-code-foundations-front',
        ],
        to: String(to?.name || 'unknown'),
      },
    )
  }
})
